import React, { useEffect, useState, useCallback } from 'react';
import { api, getIstQuarter, getSchoolYear, getAssessmentRegime } from '../../Api';
import {
    AuditContainer,
    AuditHead,
    AuditTitle,
    AuditFilters,
    FilterSelect,
    DateField,
    DateSep,
    ClearDates,
    AuditList,
    AuditRow,
    AuditInfo,
    AuditAction,
    AuditMeta,
    AuditBadge,
    RevertButton,
    RevertedTag,
    AuditEmpty,
    AuditLoading,
    Spinner,
    AuditFeedback,
    LoadMoreButton,
    ConfirmOverlay,
    ConfirmCard,
    ConfirmTitle,
    ConfirmBlock,
    ConfirmLabel,
    ConfirmValue,
    ConfirmWarn,
    ConfirmActions,
    ConfirmCancel,
    ConfirmOk,
} from './style';

/*
 * Painel de Histórico de Movimentações (reutilizável).
 * Consome:
 *   GET  /audit                 (lista, com filtros opcionais)
 *   POST /audit/revert/:id      (reverte; trata 409 = conflito)
 *
 * Props:
 *   canManage  (bool)   — se false, o componente não renderiza nada.
 *   entidadeFixa (str)  — opcional: trava o filtro numa área (ex: 'turma')
 *                         e esconde o seletor. Útil para embutir em outra tela.
 *   entidadeId (str)    — opcional: filtra por um registro específico.
 *
 * OBS sobre o caminho da api: este componente vive em src/components/AuditPanel/.
 * O import acima usa '../../Api' assumindo que o Api está em src/Api. Se a sua
 * estrutura for diferente, ajuste apenas essa linha.
 */

// Somente ações que o servidor REALMENTE registra (audit(...) em routes.js + REVERTER).
const ACAO_LABEL = {
    FECHAR_DIARIO: 'Fechou diário',
    REABRIR_DIARIO: 'Reabriu diário',
    ATUALIZAR_ESCOLA: 'Atualizou escola',
    ATUALIZAR_REGIME: 'Alterou regime',
    ATUALIZAR_ANO_LETIVO: 'Alterou ano letivo',
    CRIAR_TURMA: 'Criou turma',
    ATUALIZAR_TURMA: 'Atualizou turma',
    CRIAR_MATERIA: 'Criou matéria',
    ATUALIZAR_MATERIA: 'Atualizou matéria',
    EXCLUIR_MATERIA: 'Excluiu matéria',
    REORDENAR_MATERIAS: 'Reordenou matérias',
    VINCULAR_MATERIA: 'Vinculou matéria a professor',
    DESVINCULAR_MATERIA: 'Desvinculou matéria de professor',
    CRIAR_HISTORICO_MANUAL: 'Criou histórico',
    ATUALIZAR_HISTORICO: 'Atualizou histórico',
    ATUALIZAR_HISTORICO_MANUAL: 'Atualizou histórico manual',
    CRIAR_FICHA_ANUAL: 'Criou ficha anual',
    ATUALIZAR_FICHA_ANUAL: 'Atualizou ficha anual',
    EXCLUIR_FICHA_ANUAL: 'Excluiu ficha anual',
    GERAR_FICHAS_CONCEITO: 'Gerou fichas anuais',
    CRIAR_CERTIFICADO: 'Criou certificado',
    ATUALIZAR_QUARTER_1: 'Atualizou 1º período',
    ATUALIZAR_QUARTER_2: 'Atualizou 2º período',
    ATUALIZAR_QUARTER_3: 'Atualizou 3º período',
    ATUALIZAR_QUARTER_4: 'Atualizou 4º período',
    ATUALIZAR_QUARTER_5: 'Atualizou 5º período',
    ATUALIZAR_QUARTER_6: 'Atualizou 6º período',
    ADICIONAR_REGENTE: 'Adicionou regente',
    ADICIONAR_ADJUNTO: 'Adicionou professor adjunto',
    ADICIONAR_ED_FISICA: 'Adicionou professor de Ed. Física',
    REMOVER_REGENTE: 'Removeu regente',
    REMOVER_ADJUNTO: 'Removeu professor adjunto',
    REMOVER_ED_FISICA: 'Removeu professor de Ed. Física',
    ADICIONAR_ALUNO: 'Adicionou aluno à turma',
    REMOVER_ALUNO: 'Removeu aluno da turma',
    REMANEJAR_ALUNO: 'Remanejou aluno',
    RETORNAR_ALUNO: 'Retornou aluno',
    CADASTRAR_ALUNO: 'Cadastrou aluno',
    ATUALIZAR_ALUNO: 'Atualizou cadastro do aluno',
    ALTERAR_STATUS_ALUNO: 'Alterou situação do aluno',
    EXCLUIR_ALUNO: 'Excluiu aluno',
    CADASTRAR_FUNCIONARIO: 'Cadastrou funcionário',
    ATUALIZAR_FUNCIONARIO: 'Atualizou funcionário',
    EXCLUIR_FUNCIONARIO: 'Excluiu funcionário',
    CONFIGURAR_RECUPERACAO: 'Configurou a recuperação',
    LANCAR_RECUPERACAO: 'Lançou recuperação',
    LANCAR_RECUPERACAO_FINAL: 'Lançou recuperação final',
    ATUALIZAR_GRADE_HORARIA: 'Salvou horário da turma',
    REGISTRAR_OCORRENCIA: 'Registrou ocorrência',
    EDITAR_OCORRENCIA: 'Editou ocorrência',
    EXCLUIR_OCORRENCIA: 'Excluiu ocorrência',
    REMOVER_GRADE_HORARIA: 'Removeu horário da turma',
    REVERTER: 'Reverteu movimentação',
};

// A mudança de situação do aluno (transferir, inativar, ativar, concluir) é
// registrada como ALTERAR_STATUS_ALUNO; o rótulo vem da situação escolhida.
const STATUS_LABEL = {
    transferido: 'Transferiu aluno',
    inativo: 'Inativou aluno',
    ativo: 'Ativou aluno',
    concluido: 'Concluiu aluno',
};

// Converte datas "aaaa-mm-dd" dentro do texto para "dd/mm/aaaa" (registros antigos)
const datasBR = (txt) =>
    txt ? String(txt).replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, '$3/$2/$1') : txt;

const rotuloAcao = (log) => {
    if (log?.acao === 'ALTERAR_STATUS_ALUNO') {
        const st = String(log?.dados_depois?.status || '').toLowerCase();
        if (STATUS_LABEL[st]) return STATUS_LABEL[st];
    }
    return ACAO_LABEL[log?.acao] || log?.acao;
};

// Somente áreas que realmente aparecem nos registros
const ENTIDADES = [
    { value: '', label: 'Todas as áreas' },
    { value: 'escola', label: 'Escola' },
    { value: 'turma', label: 'Turmas' },
    { value: 'professor_turma', label: 'Professores nas turmas' },
    { value: 'aluno_turma', label: 'Alunos nas turmas' },
    { value: 'aluno', label: 'Alunos (cadastro e situação)' },
    { value: 'funcionario', label: 'Funcionários' },
    { value: 'materia', label: 'Matérias' },
    { value: 'materia_professor', label: 'Matérias de professores' },
    { value: 'historico', label: 'Históricos' },
    { value: 'ficha_anual', label: 'Fichas anuais' },
    { value: 'certificado', label: 'Certificados' },
    { value: 'quarter', label: 'Períodos' },
    { value: 'diario', label: 'Diários' },
    { value: 'recuperacao', label: 'Recuperação' },
    { value: 'ocorrencia', label: 'Ocorrências' },
];

// Filtro por tipo de ação → códigos reais enviados ao servidor.
// Códigos "STATUS:xxx" filtram ALTERAR_STATUS_ALUNO pela situação escolhida.
const TIPOS_ACAO = [
    { value: '', label: 'Todas as ações', acoes: [] },
    { value: 'atualizou', label: 'Atualizou', acoes: ['ATUALIZAR_ESCOLA', 'ATUALIZAR_TURMA', 'ATUALIZAR_MATERIA', 'ATUALIZAR_HISTORICO', 'ATUALIZAR_HISTORICO_MANUAL', 'ATUALIZAR_FICHA_ANUAL', 'ATUALIZAR_ALUNO', 'ATUALIZAR_FUNCIONARIO', 'ATUALIZAR_QUARTER_1', 'ATUALIZAR_QUARTER_2', 'ATUALIZAR_QUARTER_3', 'ATUALIZAR_QUARTER_4', 'ATUALIZAR_QUARTER_5', 'ATUALIZAR_QUARTER_6'] },
    { value: 'cadastrou', label: 'Cadastrou', acoes: ['CADASTRAR_ALUNO', 'CADASTRAR_FUNCIONARIO'] },
    { value: 'criou', label: 'Criou', acoes: ['CRIAR_TURMA', 'CRIAR_MATERIA', 'CRIAR_HISTORICO_MANUAL', 'CRIAR_FICHA_ANUAL', 'CRIAR_CERTIFICADO'] },
    { value: 'fechou', label: 'Fechou diário', acoes: ['FECHAR_DIARIO'] },
    { value: 'reabriu', label: 'Reabriu diário', acoes: ['REABRIR_DIARIO'] },
    { value: 'adicionou', label: 'Adicionou', acoes: ['ADICIONAR_REGENTE', 'ADICIONAR_ADJUNTO', 'ADICIONAR_ED_FISICA', 'ADICIONAR_ALUNO'] },
    { value: 'removeu', label: 'Removeu', acoes: ['REMOVER_REGENTE', 'REMOVER_ADJUNTO', 'REMOVER_ED_FISICA', 'REMOVER_ALUNO'] },
    { value: 'remanejou', label: 'Remanejou aluno', acoes: ['REMANEJAR_ALUNO'] },
    { value: 'transferiu', label: 'Transferiu aluno', acoes: ['STATUS:transferido'] },
    { value: 'retornou', label: 'Retornou aluno', acoes: ['RETORNAR_ALUNO'] },
    { value: 'inativou', label: 'Inativou aluno', acoes: ['STATUS:inativo'] },
    { value: 'ativou', label: 'Ativou aluno', acoes: ['STATUS:ativo'] },
    { value: 'concluiu', label: 'Concluiu aluno', acoes: ['STATUS:concluido'] },
    { value: 'excluiu', label: 'Excluiu', acoes: ['EXCLUIR_ALUNO', 'EXCLUIR_FUNCIONARIO', 'EXCLUIR_MATERIA', 'EXCLUIR_FICHA_ANUAL'] },
    { value: 'alterou', label: 'Alterou regime / ano letivo', acoes: ['ATUALIZAR_REGIME', 'ATUALIZAR_ANO_LETIVO'] },
    { value: 'vinculou', label: 'Vinculou matéria', acoes: ['VINCULAR_MATERIA'] },
    { value: 'desvinculou', label: 'Desvinculou matéria', acoes: ['DESVINCULAR_MATERIA'] },
    { value: 'reordenou', label: 'Reordenou matérias', acoes: ['REORDENAR_MATERIAS'] },
    { value: 'gerou', label: 'Gerou fichas anuais', acoes: ['GERAR_FICHAS_CONCEITO'] },
    { value: 'recuperacao', label: 'Recuperação (lançou / configurou)', acoes: ['LANCAR_RECUPERACAO', 'LANCAR_RECUPERACAO_FINAL', 'CONFIGURAR_RECUPERACAO'] },
    { value: 'horario', label: 'Horário da turma (salvou / removeu)', acoes: ['ATUALIZAR_GRADE_HORARIA', 'REMOVER_GRADE_HORARIA'] },
    { value: 'ocorrencia', label: 'Ocorrências (registrou / editou / excluiu)', acoes: ['REGISTRAR_OCORRENCIA', 'EDITAR_OCORRENCIA', 'EXCLUIR_OCORRENCIA'] },
];

const PAGE_SIZE = 15;

// ⛔ TEMPORÁRIO: botão "Reverter" desativado.
// Para reativar, mude para true.
const REVERTER_HABILITADO = false;

const AuditPanel = ({ canManage, entidadeFixa = '', entidadeId = '' }) => {
    const [logs, setLogs] = useState([]);
    const [entidade, setEntidade] = useState(entidadeFixa);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);        // "carregar mais"
    const [initialLoading, setInitialLoading] = useState(true); // primeira carga / troca de filtro
    const [revertingId, setRevertingId] = useState(null);
    const [confirmando, setConfirmando] = useState(null); // log aguardando confirmação
    const [feedback, setFeedback] = useState(null); // { tipo: 'ok'|'erro', texto }
    const [desde, setDesde] = useState(''); // data inicial (YYYY-MM-DD)
    const [ate, setAte] = useState('');     // data final (YYYY-MM-DD)
    const [tipoAcao, setTipoAcao] = useState(''); // verbo: Atualizou, Fechou...

    // Regime de avaliação (BIMESTRAL/TRIMESTRAL) para rotular os períodos.
    // Regra: usa o regime do 1º período registrado no ANO da escola; se não
    // houver, cai para o regime da escola (getAssessmentRegime / storage).
    const [regime, setRegime] = useState(
        (sessionStorage.getItem('assessmentRegime') ||
            localStorage.getItem('assessmentRegime') || '').toUpperCase()
    );

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const idSchoolRaw = sessionStorage.getItem('id-school');
                const idSchool = idSchoolRaw ? JSON.parse(idSchoolRaw) : null;
                if (idSchool == null) return;

                // ano letivo atual da escola
                let ano = null;
                try {
                    const yr = await getSchoolYear(idSchool);
                    ano = yr?.data?.data ?? null;
                } catch { /* ignora */ }

                // 1º período registrado no ano -> regime
                if (ano != null) {
                    try {
                        const res = await getIstQuarter(ano, idSchool);
                        const first = (res?.data?.data || []).find((r) => r);
                        if (!cancelled && first?.assessmentRegime) {
                            setRegime(String(first.assessmentRegime).toUpperCase());
                            return;
                        }
                    } catch { /* segue para o fallback */ }
                }

                // fallback: regime da escola (storage já preenchido no login) ou API
                let esc = sessionStorage.getItem('assessmentRegime') ||
                    localStorage.getItem('assessmentRegime');
                if (!esc) {
                    try {
                        const r = await getAssessmentRegime(idSchool);
                        esc = r?.data?.data;
                    } catch { /* ignora */ }
                }
                if (!cancelled && esc) setRegime(String(esc).toUpperCase());
            } catch (e) {
                console.error('Falha ao resolver o regime no AuditPanel:', e);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Converte rótulos de período conforme o regime: no TRIMESTRAL, troca
    // "BIMESTRE" por "TRIMESTRE" preservando maiúsculas/minúsculas.
    const trimestralize = (txt) => {
        if (!txt || regime !== 'TRIMESTRAL') return txt;
        return String(txt).replace(/bimestre/gi, (m) => {
            if (m === m.toUpperCase()) return 'TRIMESTRE';
            if (m[0] === m[0].toUpperCase()) return 'Trimestre';
            return 'trimestre';
        });
    };

    const fetchLogs = useCallback(async (pageToLoad, ent, append) => {
        // append=false => é carga inicial/refiltro (mostra spinner grande)
        // append=true  => é "carregar mais" (mostra spinner no botão)
        if (append) setLoading(true);
        else setInitialLoading(true);
        try {
            const params = { page: pageToLoad, limit: PAGE_SIZE };
            if (ent) params.entidade = ent;
            if (entidadeId) params.entidade_id = entidadeId;
            // filtros de data: 'ate' inclui o dia inteiro (até 23:59:59)
            if (desde) params.desde = new Date(desde + 'T00:00:00').toISOString();
            if (ate) params.ate = new Date(ate + 'T23:59:59').toISOString();
            const tipo = TIPOS_ACAO.find(t => t.value === tipoAcao);
            if (tipo && tipo.acoes.length) params.acoes = tipo.acoes.join(',');

            const res = await api.get('/audit', { params });
            const data = res?.data?.data || [];
            setTotal(res?.data?.total || 0);

            setLogs((prev) => (append ? [...prev, ...data] : data));
        } catch (err) {
            console.error('Erro ao carregar histórico:', err);
            setFeedback({ tipo: 'erro', texto: 'Não foi possível carregar o histórico.' });
        } finally {
            if (append) setLoading(false);
            else setInitialLoading(false);
        }
    }, [entidadeId, desde, ate, tipoAcao]);

    useEffect(() => {
        setPage(1);
        fetchLogs(1, entidade, false);
    }, [entidade, desde, ate, tipoAcao, fetchLogs]);

    useEffect(() => {
        if (!feedback) return;
        const t = setTimeout(() => setFeedback(null), 5000);
        return () => clearTimeout(t);
    }, [feedback]);

    const handleLoadMore = () => {
        const next = page + 1;
        setPage(next);
        fetchLogs(next, entidade, true);
    };

    // executa a reversão de fato (chamada pelo modal, após confirmar)
    const executarRevert = async (log) => {
        setConfirmando(null);
        if (revertingId) return;
        setRevertingId(log._id);
        setFeedback(null);
        try {
            await api.post(`/audit/revert/${log._id}`);
            setFeedback({ tipo: 'ok', texto: 'Movimentação revertida com sucesso.' });
            setPage(1);
            await fetchLogs(1, entidade, false);
        } catch (err) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.message;
            if (status === 409) {
                setFeedback({ tipo: 'erro', texto: msg || 'Não foi possível reverter: conflito com registro existente.' });
            } else {
                setFeedback({ tipo: 'erro', texto: msg || 'Erro ao reverter a movimentação.' });
            }
        } finally {
            setRevertingId(null);
        }
    };

    if (!canManage) return null;

    // Camada extra: só DIRETOR/SUPERVISOR vê o histórico, mesmo que a página
    // passe canManage por engano. Lê o cargo do storage da sessão.
    const cargo = (localStorage.getItem('position_at_school') || '').toUpperCase();
    const ehDiretorSupervisor =
        cargo.includes('DIRETOR') || cargo.includes('SUPERVISOR');
    if (!ehDiretorSupervisor) return null;

    const formatDate = (iso) => {
        try {
            return new Date(iso).toLocaleString('pt-BR', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        } catch {
            return '';
        }
    };

    // ano letivo da turma: vem resolvido do backend (__anoTurma) para qualquer
    // movimentação ligada a uma turma (diário, aluno_turma, turma...).
    // Fallback: tenta o próprio snapshot, caso o backend não tenha resolvido.
    const anoDaTurma = (log) => {
        if (log.__anoTurma) return String(log.__anoTurma);
        const fonte = log.dados_depois || log.dados_antes;
        return fonte && fonte.year ? String(fonte.year) : null;
    };

    // Ações sem botão reverter no painel:
    // - REVERTER: não faz sentido reverter uma reversão por aqui
    // - FECHAR_DIARIO: tem fluxo próprio de reabrir na tela de diário
    // - ALTERAR_STATUS_ALUNO: mudança de situação do aluno não é revertida pelo painel
    // - CRIAR_*: criação não é revertida pelo painel (regra do sistema)
    const naoReversiveis = new Set(['REVERTER', 'FECHAR_DIARIO', 'REABRIR_DIARIO', 'ALTERAR_STATUS_ALUNO', 'CADASTRAR_ALUNO']);
    const ehCriacao = (acao) => typeof acao === 'string' && acao.startsWith('CRIAR_');

    // explica, em texto claro, o que a reversão fará conforme o tipo de operação
    const implicacaoDaReversao = (log) => {
        switch (log.op) {
            case 'UPDATE':
                return 'Os dados voltarão a ser exatamente como estavam ANTES desta alteração. Qualquer mudança feita depois nesta alteração será perdida.';
            case 'DELETE':
                return 'O registro que foi excluído será recriado com todos os seus dados. Se já existir outro registro no lugar, a reversão será bloqueada.';
            case 'REORDER':
                return 'A ordem dos itens voltará a ser a ordem anterior a esta movimentação.';
            default:
                return 'A ação será desfeita e o estado anterior será restaurado.';
        }
    };

    return (
        <AuditContainer>
            <AuditHead>
                <AuditTitle>🕓 Histórico de Movimentações</AuditTitle>
                {!entidadeFixa && (
                    <AuditFilters>
                        <FilterSelect
                            value={entidade}
                            onChange={(e) => setEntidade(e.target.value)}
                        >
                            {ENTIDADES.map((op) => (
                                <option key={op.value} value={op.value}>{op.label}</option>
                            ))}
                        </FilterSelect>
                        <FilterSelect
                            value={tipoAcao}
                            onChange={(e) => setTipoAcao(e.target.value)}
                            title="Tipo de ação"
                        >
                            {TIPOS_ACAO.map((op) => (
                                <option key={op.value} value={op.value}>{op.label}</option>
                            ))}
                        </FilterSelect>
                        <DateField
                            type="date"
                            value={desde}
                            max={ate || undefined}
                            onChange={(e) => setDesde(e.target.value)}
                            title="Data inicial"
                        />
                        <DateSep>até</DateSep>
                        <DateField
                            type="date"
                            value={ate}
                            min={desde || undefined}
                            onChange={(e) => setAte(e.target.value)}
                            title="Data final"
                        />
                        {(desde || ate) && (
                            <ClearDates
                                onClick={() => { setDesde(''); setAte(''); }}
                                title="Limpar datas"
                            >
                                ✕
                            </ClearDates>
                        )}
                    </AuditFilters>
                )}
            </AuditHead>

            {feedback && (
                <AuditFeedback $tipo={feedback.tipo}>
                    {feedback.texto}
                </AuditFeedback>
            )}

            {initialLoading ? (
                <AuditLoading>
                    <Spinner />
                    <span>Carregando movimentações…</span>
                </AuditLoading>
            ) : logs.length === 0 ? (
                <AuditEmpty>Nenhuma movimentação registrada ainda.</AuditEmpty>
            ) : (
                <AuditList>
                    {logs.map((log) => {
                        const jaRevertido = log.revertido;
                        const podeReverter =
                            REVERTER_HABILITADO &&
                            !jaRevertido &&
                            !naoReversiveis.has(log.acao) &&
                            !ehCriacao(log.acao) &&
                            log.op !== 'REGISTRO' &&
                            !!log.model_ref;

                        return (
                            <AuditRow key={log._id} $revertido={jaRevertido}>
                                <AuditInfo>
                                    <AuditAction>
                                        {trimestralize(rotuloAcao(log))}
                                        {log.descricao ? <span> — {trimestralize(datasBR(log.descricao))}</span> : null}
                                        {anoDaTurma(log) && (
                                            <AuditBadge>Ano da Turma: {anoDaTurma(log)}</AuditBadge>
                                        )}
                                    </AuditAction>
                                    <AuditMeta>
                                        <AuditBadge>{log.usuario_nome || 'Usuário desconhecido'}</AuditBadge>

                                        <span>{formatDate(log.createdAt)}</span>
                                    </AuditMeta>
                                </AuditInfo>

                                {jaRevertido ? (
                                    <RevertedTag>revertido</RevertedTag>
                                ) : podeReverter ? (
                                    <RevertButton
                                        onClick={() => setConfirmando(log)}
                                        disabled={revertingId === log._id}
                                    >
                                        {revertingId === log._id ? 'Revertendo…' : 'Reverter'}
                                    </RevertButton>
                                ) : null}
                            </AuditRow>
                        );
                    })}
                </AuditList>
            )}

            {!initialLoading && logs.length < total && (
                <LoadMoreButton onClick={handleLoadMore} disabled={loading}>
                    {loading
                        ? 'Carregando…'
                        : `Carregar mais (${logs.length} de ${total})`}
                </LoadMoreButton>
            )}

            {/* ── Modal de confirmação de reversão ── */}
            {confirmando && (
                <ConfirmOverlay onClick={() => setConfirmando(null)}>
                    <ConfirmCard onClick={(e) => e.stopPropagation()}>
                        <ConfirmTitle>⚠️ Confirmar reversão</ConfirmTitle>

                        <ConfirmBlock>
                            <ConfirmLabel>Ação que será revertida:</ConfirmLabel>
                            <ConfirmValue>
                                {trimestralize(rotuloAcao(confirmando))}
                                {confirmando.descricao ? ` — ${trimestralize(datasBR(confirmando.descricao))}` : ''}
                            </ConfirmValue>
                        </ConfirmBlock>

                        <ConfirmBlock>
                            <ConfirmLabel>Feita por:</ConfirmLabel>
                            <ConfirmValue>
                                {confirmando.usuario_nome || 'Usuário desconhecido'}
                                {' em '}
                                {formatDate(confirmando.createdAt)}
                            </ConfirmValue>
                        </ConfirmBlock>

                        <ConfirmWarn>
                            <strong>O que vai acontecer:</strong> {implicacaoDaReversao(confirmando)}
                        </ConfirmWarn>

                        <ConfirmActions>
                            <ConfirmCancel onClick={() => setConfirmando(null)}>
                                Cancelar
                            </ConfirmCancel>
                            <ConfirmOk onClick={() => executarRevert(confirmando)}>
                                Sim, reverter
                            </ConfirmOk>
                        </ConfirmActions>
                    </ConfirmCard>
                </ConfirmOverlay>
            )}
        </AuditContainer>
    );
};

export default AuditPanel;